import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Target,
  TrendingUp,
  UserRound,
  Zap,
} from "lucide-react";
import {
  Answer,
  AssessmentHistoryQuestionReview,
  AssessmentLeaderboardItem,
  AssessmentQuestionCategory,
  AssessmentQuestionType,
  AssessmentStatResponse,
  AssessmentStatSummary,
} from "../../../axios/quiz";
import { useAuthStore } from "../../../stores/authStore";
import useUserStore from "../../../stores/userStore";
import {
  getAssessmentUserPlace,
  sortAssessmentLeaderboard,
} from "../assessment-contest-utils";
import {
  formatAssessmentScore,
  formatAssessmentScoreFraction,
  formatAssessmentScoreNumber,
  formatAssessmentUserScorePoints,
} from "../../../utils/assessmentScore";
import { QuizCountdownCircle } from "../quiz-countdown-circle/quiz-countdown-circle";
import {
  QuizResultsCircular,
  type AssessmentRunningScoreBreakdown,
} from "../quiz-results-circular/quiz-results-circular";
import { AssessmentContestTopBar } from "../assessment-contest-top-bar/assessment-contest-top-bar";
import {
  buildFillInBlankUserAnswerSegments,
  type QuestionPreviewSegment,
} from "../../../utils/assessmentInteractive";
import {
  answerListStyles,
  answerOptionStyles,
  chosenAnswerLabelStyles,
  itemMetaStyles,
  justificationBoxStyles,
  openAnswerBoxStyles,
  questionCardStyles,
  questionIndexStyles,
  questionLabelRowStyles,
  questionTextStyles,
  scoreBadgeStyles,
} from "../../assessment-history-modal/styles";
import {
  confettiOverlayStyle,
  containerStyle,
  contentStyle,
  footerActionsStyle,
  footerStyle,
  headerCirclesStyle,
  headerStyle,
  linearProgressStyle,
  metricsRowStyle,
  podiumBadgeStyle,
  podiumBaseWrapStyle,
  podiumBaseStyle,
  podiumCardStyle,
  podiumGridStyle,
  podiumItemWrapStyle,
  podiumNameStyle,
  podiumPointsStyle,
  positionCardStyle,
  positionIconWrapStyle,
  positionMetaStyle,
  positionScoreStyle,
  positionTitleStyle,
  scoreBreakdownCardStyle,
  scoreBreakdownRowStyle,
  scoreBreakdownValueStyle,
  primaryActionStyle,
  progressBlockStyle,
  progressHeaderRowStyle,
  progressLabelStyle,
  resultsStateBadgeStyle,
  secondaryActionStyle,
  areaInsightCardStyle,
  areaInsightIconWrapStyle,
  areaInsightLabelStyle,
  areaInsightMetaStyle,
  areaInsightsContainerStyle,
  areaInsightsGridStyle,
  areaInsightTextWrapStyle,
  areaInsightTitleStyle,
  summaryCardStyle,
  summaryCardsGridStyle,
  summaryIconWrapStyle,
  summaryMetaStyle,
  summaryNameStyle,
  summaryTextWrapStyle,
  summaryTitleStyle,
  winnersInnerStyle,
  winnersSectionStyle,
  winnersSubtitleStyle,
  winnersTitleStyle,
  questionFeedbackListStyle,
  questionFeedbackSectionStyle,
  questionFeedbackToggleButtonStyle,
  questionFeedbackToggleIconStyle,
} from "./styles";
import placePodium from "../../../assets/images/place-podium.png";

const INTERACTIVE_FEEDBACK_TYPES = new Set([
  "fill_in_blank",
  "ordering",
  "matching",
  "ordered_response",
]);

type FeedbackAnswerVariant =
  | "chosen-correct"
  | "chosen-wrong"
  | "correct-other"
  | "neutral";

const normalizeAnswerText = (value: string): string => value.trim();

const getFeedbackAnswerVariant = (
  item: AssessmentQuestionFeedbackItem,
  answerText: string,
  answerPoints: number,
): FeedbackAnswerVariant => {
  const isChosen =
    normalizeAnswerText(answerText) ===
    normalizeAnswerText(item.chosenAnswer);

  if (isChosen) {
    return answerPoints > 0 || item.isCorrect
      ? "chosen-correct"
      : "chosen-wrong";
  }

  if (answerPoints > 0) {
    return "correct-other";
  }

  return "neutral";
};

const renderFeedbackSegments = (
  segments: QuestionPreviewSegment[] | undefined,
  fallback: string,
) => {
  if (segments?.length) {
    return segments.map((segment, index) =>
      segment.bold ? (
        <Box
          component="span"
          key={`feedback-segment-${index}`}
          sx={{ fontWeight: 800 }}
        >
          {segment.text}
        </Box>
      ) : (
        <React.Fragment key={`feedback-segment-${index}`}>
          {segment.text}
        </React.Fragment>
      ),
    );
  }
  return fallback || "—";
};

export type AssessmentQuestionFeedbackItem = {
  questionIndex: number;
  questionText: string;
  questionSegments?: QuestionPreviewSegment[];
  questionType?: AssessmentQuestionType;
  chosenAnswer: string;
  chosenAnswerSegments?: QuestionPreviewSegment[];
  answers?: Answer[] | null;
  score: number;
  maxScore: number;
  bonusScore?: number;
  isCorrect: boolean;
  justification?: string | null;
  isBonus?: boolean;
  questionCategory?: AssessmentQuestionCategory;
};

export const mapHistoryQuestionsToFeedbackItems = (
  questions: AssessmentHistoryQuestionReview[],
): AssessmentQuestionFeedbackItem[] =>
  [...questions]
    .sort((a, b) => a.question_index - b.question_index)
    .map((question) => {
      const isFillInBlank = question.question_type === "fill_in_blank";
      // Question: template with blank placeholders. Answer: sentence with inserts.
      const fillInBlankQuestionSegments = isFillInBlank
        ? buildFillInBlankUserAnswerSegments(question.question, "")
        : undefined;
      const fillInBlankAnswerSegments =
        isFillInBlank && question.chosen_answer.trim()
          ? buildFillInBlankUserAnswerSegments(
              question.question,
              question.chosen_answer,
            )
          : undefined;

      return {
        questionIndex: Math.max(0, question.question_index - 1),
        questionText: question.question,
        questionSegments: fillInBlankQuestionSegments,
        questionType: question.question_type,
        chosenAnswer: question.chosen_answer,
        chosenAnswerSegments: fillInBlankAnswerSegments,
        answers: question.answers,
        score: question.score,
        maxScore: question.max_score,
        bonusScore: question.bonus_score,
        isCorrect: question.is_correct,
        justification: question.justification,
        isBonus: question.is_bonus,
        questionCategory: question.question_category,
      };
    });

interface AssessmentContestResultsProps {
  totalQuestions: number;
  answeredQuestions: number;
  totalMaxScore: number;
  remainingSeconds?: number;
  totalSeconds?: number;
  leaderboard?: AssessmentLeaderboardItem[];
  currentUserPlace?: number | null;
  assessmentStats?: AssessmentStatResponse | null;
  scoreBreakdown?: AssessmentRunningScoreBreakdown | null;
  questionFeedbacks?: AssessmentQuestionFeedbackItem[];
  gamificationEnabled?: boolean;
  contestTitle?: string | null;
  onReviewPerformance: () => void;
  onClose: () => void;
  managerActions?: React.ReactNode;
}

const pickString = (
  summary: AssessmentStatSummary | null | undefined,
  keys: string[],
) => {
  if (!summary) {
    return null;
  }

  for (const key of keys) {
    const value = summary[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
};

const pickNumber = (
  summary: AssessmentStatSummary | Record<string, unknown> | null | undefined,
  keys: string[],
) => {
  if (!summary) {
    return null;
  }

  for (const key of keys) {
    const value = summary[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
};

const formatSeconds = (value: number | null, isSettled: boolean) => {
  if (value === null) {
    return isSettled
      ? "Fastest correct answer: -"
      : "Fastest correct answer: updating...";
  }

  const normalized = value.toFixed(1).replace(/\.0$/, "");
  return `Fastest correct answer: ${normalized} sec`;
};

const formatAccuracy = (value: number | null, isSettled: boolean) => {
  if (value === null) {
    return isSettled ? "Accuracy: -" : "Accuracy: updating...";
  }

  const percent = value <= 1 ? value * 100 : value;
  return `Accuracy: ${Math.round(percent)}%`;
};

const normalizeConceptStat = (
  value: unknown,
): AssessmentStatSummary | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return { concept: value.trim() };
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as AssessmentStatSummary;
  }

  return null;
};

const getNestedStatSummary = (
  source: AssessmentStatSummary | null | undefined,
  keys: string[],
): AssessmentStatSummary | null => {
  if (!source) {
    return null;
  }

  for (const key of keys) {
    const normalized = normalizeConceptStat(source[key]);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const parseFractionFromText = (text: string | null) => {
  if (!text) {
    return null;
  }

  const match = text.match(/(\d+)\s*\/\s*(\d+)/);
  if (!match) {
    return null;
  }

  return {
    correct: Number(match[1]),
    total: Number(match[2]),
  };
};

const CONCEPT_TITLE_KEYS = [
  "concept",
  "concept_name",
  "competency",
  "area",
  "area_name",
  "name",
  "title",
  "label",
];

const getConceptTitle = (summary: AssessmentStatSummary | null | undefined) =>
  pickString(summary, CONCEPT_TITLE_KEYS);

const getConceptBreakdownList = (
  conceptSummary: AssessmentStatSummary | Record<string, unknown> | null | undefined,
): AssessmentStatSummary[] => {
  if (!conceptSummary) {
    return [];
  }

  const listKeys = [
    "concepts",
    "concept_stats",
    "concept_breakdown",
    "items",
    "by_concept",
    "performance_by_concept",
    "all_concepts",
  ];

  for (const key of listKeys) {
    const value = conceptSummary[key];
    if (Array.isArray(value)) {
      return value.filter(
        (item): item is AssessmentStatSummary =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item),
      );
    }
  }

  return [];
};

const pickConceptCounts = (
  summary: AssessmentStatSummary | null | undefined,
) => {
  const correct = pickNumber(summary, [
    "correct",
    "correct_count",
    "correct_answers",
    "correct_answers_count",
    "correct_questions",
    "correct_questions_count",
    "questions_correct",
    "answered_correct",
    "answered_correctly",
    "questions_answered_correctly",
    "answered_correct_questions",
    "num_correct",
    "n_correct",
    "number_correct",
    "right_count",
    "score_correct",
  ]);

  let total = pickNumber(summary, [
    "total",
    "total_count",
    "total_questions",
    "total_questions_count",
    "total_questions_number",
    "questions_count",
    "question_count",
    "questions_total",
    "num_total",
    "n_total",
    "number_total",
    "max_questions",
    "questions_in_concept",
    "total_questions_in_concept",
  ]);

  const wrong = pickNumber(summary, [
    "wrong",
    "wrong_count",
    "incorrect",
    "incorrect_count",
  ]);

  if (correct !== null && total === null && wrong !== null) {
    total = correct + wrong;
  }

  if (correct === null || total === null) {
    const score = pickNumber(summary, ["score", "scores_earned", "points_earned"]);
    const allScore = pickNumber(summary, [
      "all_score",
      "max_score",
      "total_score",
      "total_max_score",
      "total_points",
    ]);

    if (score !== null && allScore !== null && allScore >= score) {
      return { correct: score, total: allScore };
    }
  }

  return { correct, total };
};

const mergeConceptInsight = (
  stats: AssessmentStatResponse | null | undefined,
  conceptKey: "strongest_concept" | "needs_review_concept",
): AssessmentStatSummary | null => {
  if (!stats) {
    return null;
  }

  const record = stats as Record<string, unknown>;
  const raw = record[conceptKey];
  const prefix = conceptKey === "strongest_concept" ? "strongest" : "needs_review";

  let base = normalizeConceptStat(raw);
  const conceptSummary: Record<string, unknown> | undefined = stats.concept_summary
    ? (stats.concept_summary as Record<string, unknown>)
    : Array.isArray(record.concepts)
      ? { concepts: record.concepts }
      : undefined;

  if (conceptSummary) {
    const summaryRecord = conceptSummary;
    const detailed = normalizeConceptStat(summaryRecord[conceptKey]);

    if (detailed) {
      const conceptTitle =
        typeof raw === "string" ? raw.trim() : getConceptTitle(base);
      base = {
        ...detailed,
        ...(conceptTitle ? { concept: conceptTitle } : {}),
      };
    }

    const summaryCorrect = pickNumber(conceptSummary, [
      `${prefix}_correct`,
      `${prefix}_correct_count`,
      `${prefix}_questions_correct`,
      `${prefix}_correct_answers`,
    ]);
    const summaryTotal = pickNumber(conceptSummary, [
      `${prefix}_total`,
      `${prefix}_total_questions`,
      `${prefix}_questions_total`,
      `${prefix}_total_questions_count`,
    ]);

    if (base) {
      base = {
        ...base,
        ...(summaryCorrect !== null ? { correct: summaryCorrect } : {}),
        ...(summaryTotal !== null ? { total: summaryTotal } : {}),
      };
    }
  }

  const siblingStats = normalizeConceptStat(record[`${conceptKey}_stats`]);
  if (siblingStats) {
    base = base ? { ...siblingStats, ...base } : siblingStats;
  }

  const conceptTitle = getConceptTitle(base);
  if (conceptTitle && conceptSummary) {
    const matched = getConceptBreakdownList(conceptSummary).find(
      (item) => getConceptTitle(item) === conceptTitle,
    );
    if (matched) {
      base = { ...matched, ...base, concept: conceptTitle };
    }
  }

  if (!base) {
    const concepts = record.concepts;
    if (Array.isArray(concepts) && concepts.length > 0) {
      const normalizedConcepts = concepts
        .map((item) => normalizeConceptStat(item))
        .filter((item): item is AssessmentStatSummary => Boolean(item));

      if (conceptKey === "strongest_concept") {
        base = normalizedConcepts[0] ?? null;
      }
    }
  }

  return base;
};

const formatConceptScoreMeta = (earned: number, max: number): string =>
  `You scored ${earned}/${max} points`;

const formatAreaPerformance = (
  summary: AssessmentStatSummary | null | undefined,
): string | null => {
  if (!summary) {
    return null;
  }

  const nestedSummary = getNestedStatSummary(summary, [
    "stats",
    "performance",
    "summary",
    "details",
    "detail",
    "answered",
    "questions",
    "question_stats",
    "scores",
  ]);
  if (nestedSummary) {
    const nestedText = formatAreaPerformance(nestedSummary);
    if (nestedText) {
      return nestedText;
    }
  }

  const directText = pickString(summary, [
    "performance_text",
    "performance_summary",
    "subtitle",
    "description",
    "detail",
    "detail_text",
    "message",
    "summary",
    "result",
    "score_text",
    "stat",
  ]);
  if (directText) {
    if (/you answered/i.test(directText)) {
      return directText;
    }

    const fractionFromText = parseFractionFromText(directText);
    if (fractionFromText) {
      return formatConceptScoreMeta(
        fractionFromText.correct,
        fractionFromText.total,
      );
    }

    return directText;
  }

  const { correct, total } = pickConceptCounts(summary);

  if (correct !== null && total !== null) {
    return formatConceptScoreMeta(correct, total);
  }

  for (const value of Object.values(summary)) {
    if (typeof value === "string") {
      const fractionFromValue = parseFractionFromText(value);
      if (fractionFromValue) {
        return formatConceptScoreMeta(
          fractionFromValue.correct,
          fractionFromValue.total,
        );
      }
    }
  }

  return null;
};

const resolveAreaInsight = (
  summary: AssessmentStatSummary | null | undefined,
) => {
  const title = getConceptTitle(summary) ?? "Not available yet";

  return {
    title,
    meta: formatAreaPerformance(summary),
  };
};

const AreaInsightCard = ({
  variant,
  label,
  title,
  meta,
}: {
  variant: "strongest" | "needsReview";
  label: string;
  title: string;
  meta: string | null;
}) => (
  <Box sx={areaInsightCardStyle(variant)}>
    <Box sx={areaInsightIconWrapStyle(variant)}>
      {variant === "strongest" ? (
        <ArrowUpRight size={20} />
      ) : (
        <ArrowDown size={20} />
      )}
    </Box>
    <Box sx={areaInsightTextWrapStyle}>
      <Typography sx={areaInsightLabelStyle(variant)}>{label}</Typography>
      <Typography sx={areaInsightTitleStyle}>{title}</Typography>
      <Typography sx={areaInsightMetaStyle}>
        {meta ?? "No performance data yet"}
      </Typography>
    </Box>
  </Box>
);

const formatBestComeback = (
  lowestRank: number | null,
  finalRank: number | null,
  rankImprovement: number | null,
  isSettled: boolean,
) => {
  if (
    lowestRank !== null &&
    finalRank !== null &&
    Number.isFinite(lowestRank) &&
    Number.isFinite(finalRank)
  ) {
    if (lowestRank !== finalRank) {
      return `Climbed from #${lowestRank} to #${finalRank}`;
    }

    return `Finished at rank #${finalRank}`;
  }

  if (rankImprovement !== null) {
    return `Rank improved: ${rankImprovement > 0 ? "+" : ""}${rankImprovement}`;
  }

  return isSettled
    ? "Rank improved: +0"
    : "Rank improvement: updating...";
};

export const AssessmentContestResults = ({
  totalQuestions,
  answeredQuestions,
  totalMaxScore,
  remainingSeconds = 0,
  totalSeconds = 0,
  leaderboard = [],
  currentUserPlace,
  assessmentStats,
  scoreBreakdown = null,
  questionFeedbacks = [],
  gamificationEnabled = true,
  contestTitle = null,
  onReviewPerformance,
  onClose,
  managerActions,
}: AssessmentContestResultsProps) => {
  const authUserId = useAuthStore((state) => state.userId);
  const storedUserId = useUserStore((state) => state.user.id);
  const currentUserId = storedUserId || authUserId || null;
  const [isQuestionFeedbackOpen, setIsQuestionFeedbackOpen] = useState(false);

  const rawLeaderboard =
    assessmentStats?.leaderboard && assessmentStats.leaderboard.length > 0
      ? assessmentStats.leaderboard
      : leaderboard;

  // No-competition mode: never surface other participants.
  const resolvedLeaderboard = useMemo(() => {
    if (gamificationEnabled) {
      return rawLeaderboard;
    }
    if (!currentUserId) {
      return [];
    }
    return rawLeaderboard.filter((item) => item.user_id === currentUserId);
  }, [gamificationEnabled, rawLeaderboard, currentUserId]);

  const rankedLeaderboard = useMemo(() => {
    let previousRankingKey: string | null = null;
    let previousRank = 0;

    return sortAssessmentLeaderboard(resolvedLeaderboard, currentUserId).map(
      (item, index) => {
        const itemRankingKey = `${item.user_score ?? item.score}:${item.mandatory_score ?? 0}`;
        const rank =
          previousRankingKey === itemRankingKey ? previousRank : index + 1;
        previousRankingKey = itemRankingKey;
        previousRank = rank;

        return {
          ...item,
          rank,
        };
      },
    );
  }, [resolvedLeaderboard, currentUserId]);

  const topThree = rankedLeaderboard.slice(0, 3);
  const currentUserLeaderboardItem = useMemo(
    () =>
      currentUserId !== null
        ? rankedLeaderboard.find((item) => item.user_id === currentUserId)
        : null,
    [rankedLeaderboard, currentUserId],
  );
  const resolvedCurrentUserScore =
    scoreBreakdown?.userScore ??
    currentUserLeaderboardItem?.user_score ??
    currentUserLeaderboardItem?.score ??
    0;
  const resolvedCurrentUserBonusScore =
    scoreBreakdown?.gamificationBonusScore ??
    currentUserLeaderboardItem?.gamification_bonus_score ??
    currentUserLeaderboardItem?.bonus_score ??
    0;
  const resolvedMandatoryScore =
    scoreBreakdown?.mandatoryScore ??
    currentUserLeaderboardItem?.mandatory_score ??
    0;
  const resolvedMandatoryMaxScore =
    scoreBreakdown?.mandatoryMaxScore ??
    currentUserLeaderboardItem?.mandatory_max_score ??
    totalMaxScore;
  const resolvedBonusQuestionScore =
    scoreBreakdown?.bonusQuestionScore ??
    currentUserLeaderboardItem?.bonus_question_score ??
    0;
  const resolvedBonusQuestionMaxScore =
    scoreBreakdown?.bonusQuestionMaxScore ??
    currentUserLeaderboardItem?.bonus_question_max_score ??
    0;
  const resolvedScoreBreakdown: AssessmentRunningScoreBreakdown = {
    mandatoryScore: resolvedMandatoryScore,
    mandatoryMaxScore: resolvedMandatoryMaxScore,
    bonusQuestionScore: resolvedBonusQuestionScore,
    bonusQuestionMaxScore: resolvedBonusQuestionMaxScore,
    userScore: resolvedCurrentUserScore,
    gamificationBonusScore: resolvedCurrentUserBonusScore,
  };
  const isContestFinished = assessmentStats?.assessment_finished === true;
  // Timer at 00:00 (or server finished flag) means awards are final — no more "Updating...".
  const isResultsSettled = isContestFinished || remainingSeconds <= 0;
  const pendingAwardLabel = isResultsSettled ? "-" : "Updating...";
  const timerTotalSeconds = Math.max(1, totalSeconds || 1);
  const displayRemainingSeconds = isContestFinished
    ? 0
    : Math.max(0, remainingSeconds);
  const podiumItems = useMemo(() => {
    const first = topThree.find((item) => item.rank === 1);
    const second = topThree.find((item) => item.rank === 2);
    const third = topThree.find((item) => item.rank === 3);

    return [second, first, third].filter(
      (item): item is (typeof topThree)[number] => Boolean(item),
    );
  }, [topThree]);
  const resolvedCurrentUserPlace = getAssessmentUserPlace(
    resolvedLeaderboard,
    currentUserId,
    currentUserPlace ?? null,
  );
  const questionProgress =
    totalQuestions > 0
      ? Math.min(100, Math.round((answeredQuestions / totalQuestions) * 100))
      : 0;
  const resolvedPointsMaxScore = Math.max(
    resolvedMandatoryMaxScore,
    totalMaxScore,
    totalQuestions,
  );
  const fastestTapName =
    pickString(assessmentStats?.fastest_tap, [
      "name",
      "user_name",
      "username",
      "display_name",
    ]) ?? pendingAwardLabel;
  const fastestTapValue = formatSeconds(
    pickNumber(assessmentStats?.fastest_tap, [
      "time_spent",
      "fastest_time",
      "seconds",
      "time",
    ]),
    isResultsSettled,
  );

  const mostAccurateName =
    pickString(assessmentStats?.most_accurate, [
      "name",
      "user_name",
      "username",
      "display_name",
    ]) ?? pendingAwardLabel;
  const mostAccurateValue = formatAccuracy(
    pickNumber(assessmentStats?.most_accurate, [
      "accuracy",
      "accuracy_rate",
      "accuracy_percent",
      "percent",
      "percentage",
    ]),
    isResultsSettled,
  );

  const bestComebackName =
    pickString(assessmentStats?.best_comeback, [
      "name",
      "user_name",
      "username",
      "display_name",
    ]) ?? pendingAwardLabel;
  const bestComebackValue = formatBestComeback(
    pickNumber(assessmentStats?.best_comeback, [
      "lowest_rank",
      "lowestRank",
      "start_rank",
    ]),
    pickNumber(assessmentStats?.best_comeback, [
      "final_rank",
      "finalRank",
      "rank",
    ]),
    pickNumber(assessmentStats?.best_comeback, [
      "rank_improvement",
      "rankImprovement",
      "improvement",
    ]),
    isResultsSettled,
  );

  const strongestAreaSummary = mergeConceptInsight(
    assessmentStats,
    "strongest_concept",
  );
  const needsReviewAreaSummary = mergeConceptInsight(
    assessmentStats,
    "needs_review_concept",
  );
  const strongestAreaInsight = resolveAreaInsight(strongestAreaSummary);
  const needsReviewAreaInsight = resolveAreaInsight(needsReviewAreaSummary);

  return (
    <Box sx={containerStyle}>
      <Box sx={headerStyle}>
        <AssessmentContestTopBar
          contestTitle={contestTitle}
          currentUserPlace={
            gamificationEnabled ? resolvedCurrentUserPlace : null
          }
          leaderboard={resolvedLeaderboard}
          actions={managerActions}
          gamificationEnabled={gamificationEnabled}
        />
        <Box sx={metricsRowStyle}>
          <QuizCountdownCircle
            remainingSeconds={displayRemainingSeconds}
            totalSeconds={timerTotalSeconds}
          />

          <Box sx={progressBlockStyle}>
            <Box sx={progressHeaderRowStyle}>
              <Typography sx={progressLabelStyle}>Progress</Typography>
              <Typography sx={progressLabelStyle}>
                {questionProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={questionProgress}
              sx={linearProgressStyle}
            />
          </Box>

          <Box sx={headerCirclesStyle}>
            <QuizResultsCircular
              totalAnswers={totalQuestions}
              totalMaxScore={resolvedPointsMaxScore}
              scoresEarned={resolvedCurrentUserScore}
              bonusScore={resolvedCurrentUserBonusScore}
              scoreBreakdown={resolvedScoreBreakdown}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={contentStyle}>
        <Box sx={winnersSectionStyle}>
          <Box sx={confettiOverlayStyle} />
          <Box sx={winnersInnerStyle}>
            <Typography sx={winnersTitleStyle}>
              {gamificationEnabled ? "Challenge Winners" : "Your Results"}
            </Typography>
            <Typography sx={winnersSubtitleStyle}>
              {gamificationEnabled
                ? "Great job! Here are the top performers in this challenge."
                : "Great job! Here's how you did in this challenge."}
            </Typography>
            {gamificationEnabled ? (
              <Typography sx={resultsStateBadgeStyle(isContestFinished)}>
                {isContestFinished
                  ? "Final results - leaderboard locked"
                  : "Live updates - results may still change"}
              </Typography>
            ) : null}

            {gamificationEnabled || podiumItems.length > 0 ? (
              <Box sx={podiumGridStyle}>
                {podiumItems.map((item) => {
                  const isCurrentUser =
                    currentUserId !== null && item.user_id === currentUserId;
                  const place = gamificationEnabled ? item.rank : 1;

                  return (
                    <Box
                      key={item.user_id}
                      sx={podiumItemWrapStyle(place === 1)}
                    >
                      <Box sx={podiumBaseWrapStyle}>
                        <Box
                          component="img"
                          src={placePodium}
                          alt=""
                          aria-hidden="true"
                          sx={podiumBaseStyle}
                        />
                      </Box>
                      <Box sx={podiumCardStyle(place, place === 1)}>
                        {gamificationEnabled ? (
                          <Box sx={podiumBadgeStyle(place)}>{place}</Box>
                        ) : null}
                        <Typography sx={podiumNameStyle}>
                          {isCurrentUser ? "You" : item.name}
                        </Typography>
                        <Typography sx={podiumPointsStyle}>
                          {formatAssessmentUserScorePoints(
                            item.user_score ?? item.score,
                            item.gamification_bonus_score ?? item.bonus_score,
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            {gamificationEnabled ? (
              <Box sx={summaryCardsGridStyle}>
                <Box sx={summaryCardStyle}>
                  <Box
                    sx={summaryIconWrapStyle(
                      "rgba(34, 197, 94, 0.14)",
                      "#22C55E",
                    )}
                  >
                    <Zap size={20} />
                  </Box>
                  <Box sx={summaryTextWrapStyle}>
                    <Typography sx={summaryTitleStyle}>Fastest TAP</Typography>
                    <Typography sx={summaryNameStyle}>
                      {fastestTapName}
                    </Typography>
                    <Typography sx={summaryMetaStyle}>
                      {fastestTapValue}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={summaryCardStyle}>
                  <Box
                    sx={summaryIconWrapStyle(
                      "rgba(56, 189, 248, 0.14)",
                      "#0EA5E9",
                    )}
                  >
                    <Target size={20} />
                  </Box>
                  <Box sx={summaryTextWrapStyle}>
                    <Typography sx={summaryTitleStyle}>Most Accurate</Typography>
                    <Typography sx={summaryNameStyle}>
                      {mostAccurateName}
                    </Typography>
                    <Typography sx={summaryMetaStyle}>
                      {mostAccurateValue}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={summaryCardStyle}>
                  <Box
                    sx={summaryIconWrapStyle(
                      "rgba(168, 85, 247, 0.12)",
                      "#8B5CF6",
                    )}
                  >
                    <TrendingUp size={20} />
                  </Box>
                  <Box sx={summaryTextWrapStyle}>
                    <Typography sx={summaryTitleStyle}>Best Comeback</Typography>
                    <Typography sx={summaryNameStyle}>
                      {bestComebackName}
                    </Typography>
                    <Typography sx={summaryMetaStyle}>
                      {bestComebackValue}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : null}

            <Box sx={areaInsightsContainerStyle}>
              <Box sx={areaInsightsGridStyle}>
                <AreaInsightCard
                  variant="strongest"
                  label="Your strongest area"
                  title={strongestAreaInsight.title}
                  meta={strongestAreaInsight.meta}
                />
                <AreaInsightCard
                  variant="needsReview"
                  label="Needs review"
                  title={needsReviewAreaInsight.title}
                  meta={needsReviewAreaInsight.meta}
                />
              </Box>
            </Box>

            {gamificationEnabled ? (
              <Box sx={positionCardStyle}>
                <Box sx={positionIconWrapStyle}>
                  <UserRound size={22} />
                </Box>
                <Box>
                  <Typography sx={positionTitleStyle}>Your position</Typography>
                  <Typography sx={positionMetaStyle}>
                    {`Rank #${resolvedCurrentUserPlace ?? "-"} `}
                    <Box component="span" sx={{ opacity: 0.42, mx: 1 }}>
                      •
                    </Box>
                    <Box component="span" sx={positionScoreStyle}>
                      {formatAssessmentUserScorePoints(
                        resolvedCurrentUserScore,
                        resolvedCurrentUserBonusScore,
                      )}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            ) : null}

            <Box sx={scoreBreakdownCardStyle}>
              <Box sx={scoreBreakdownRowStyle}>
                <Box component="span">Mandatory score</Box>
                <Box component="span" sx={scoreBreakdownValueStyle}>
                  {formatAssessmentScoreFraction(
                    resolvedMandatoryScore,
                    resolvedMandatoryMaxScore,
                  )}{" "}
                  points
                </Box>
              </Box>
              <Box sx={scoreBreakdownRowStyle}>
                <Box component="span">Bonus questions</Box>
                <Box component="span" sx={scoreBreakdownValueStyle}>
                  {formatAssessmentScoreFraction(
                    resolvedBonusQuestionScore,
                    resolvedBonusQuestionMaxScore,
                  )}{" "}
                  points
                </Box>
              </Box>
              <Box sx={scoreBreakdownRowStyle}>
                <Box component="span">Total user score</Box>
                <Box component="span" sx={scoreBreakdownValueStyle}>
                  {formatAssessmentScoreNumber(resolvedCurrentUserScore)} pts
                </Box>
              </Box>
              <Box sx={scoreBreakdownRowStyle}>
                <Box component="span">Full mark</Box>
                <Box component="span" sx={scoreBreakdownValueStyle}>
                  {formatAssessmentScoreNumber(resolvedMandatoryMaxScore)}{" "}
                  points
                </Box>
              </Box>
              {resolvedCurrentUserBonusScore > 0 ? (
                <Box sx={scoreBreakdownRowStyle}>
                  <Box component="span">Speed/streak bonus</Box>
                  <Box component="span" sx={scoreBreakdownValueStyle}>
                    +
                    {formatAssessmentScoreNumber(
                      resolvedCurrentUserBonusScore,
                    )}{" "}
                    pts
                  </Box>
                </Box>
              ) : null}
            </Box>

            {questionFeedbacks.length > 0 ? (
              <Box sx={questionFeedbackSectionStyle}>
                <Button
                  onClick={() =>
                    setIsQuestionFeedbackOpen((prev) => !prev)
                  }
                  sx={questionFeedbackToggleButtonStyle}
                  aria-expanded={isQuestionFeedbackOpen}
                >
                  {isQuestionFeedbackOpen
                    ? "Hide Questions Feedback"
                    : "Show Questions Feedback"}
                  <Box
                    component="span"
                    sx={questionFeedbackToggleIconStyle(
                      isQuestionFeedbackOpen,
                    )}
                  >
                    <ChevronDown size={22} strokeWidth={2.75} />
                  </Box>
                </Button>

                {isQuestionFeedbackOpen ? (
                  <Box sx={questionFeedbackListStyle}>
                    {questionFeedbacks.map((item) => {
                      const maxScore = Math.max(item.maxScore, 1);
                      const isInteractive = Boolean(
                        item.questionType &&
                          INTERACTIVE_FEEDBACK_TYPES.has(item.questionType),
                      );
                      const isOpenText =
                        item.questionType === "open_text" ||
                        (!isInteractive &&
                          !item.answers?.length &&
                          item.chosenAnswer.length > 0);
                      const hasMsqAnswers = Boolean(item.answers?.length);
                      const scoreLabel = formatAssessmentScore(
                        item.score,
                        maxScore,
                        item.bonusScore,
                      );

                      return (
                        <Box
                          key={item.questionIndex}
                          sx={questionCardStyles}
                        >
                          <Box sx={questionLabelRowStyles}>
                            <Typography sx={questionIndexStyles}>
                              Question {item.questionIndex + 1}
                            </Typography>
                            <Chip
                              size="small"
                              label={item.isBonus ? "Bonus" : "Mandatory"}
                              color={item.isBonus ? "warning" : "default"}
                            />
                            <Typography
                              sx={scoreBadgeStyles(item.isCorrect)}
                            >
                              {scoreLabel}
                            </Typography>
                          </Box>

                          <Typography
                            component="div"
                            sx={questionTextStyles}
                          >
                            {renderFeedbackSegments(
                              item.questionSegments,
                              item.questionText,
                            )}
                          </Typography>

                          {isInteractive ? (
                            <>
                              <Box sx={answerListStyles}>
                                <Box
                                  sx={answerOptionStyles(
                                    item.isCorrect
                                      ? "chosen-correct"
                                      : "chosen-wrong",
                                  )}
                                >
                                  <Typography
                                    sx={chosenAnswerLabelStyles(
                                      item.isCorrect
                                        ? "chosen-correct"
                                        : "chosen-wrong",
                                    )}
                                  >
                                    Your answer
                                  </Typography>
                                  <Box
                                    component="span"
                                    sx={{ display: "block" }}
                                  >
                                    {renderFeedbackSegments(
                                      item.chosenAnswerSegments,
                                      item.chosenAnswer,
                                    )}
                                    {item.score !== 0
                                      ? ` (${formatAssessmentUserScorePoints(
                                          item.score,
                                          item.bonusScore,
                                        )})`
                                      : item.questionType === "fill_in_blank"
                                        ? " (0 pts)"
                                        : ""}
                                  </Box>
                                </Box>
                              </Box>
                              {item.justification ? (
                                <Box
                                  sx={justificationBoxStyles(item.isCorrect)}
                                >
                                  {item.justification}
                                </Box>
                              ) : null}
                            </>
                          ) : isOpenText ? (
                            <>
                              <Typography sx={itemMetaStyles}>
                                Your answer
                              </Typography>
                              <Box sx={openAnswerBoxStyles}>
                                {renderFeedbackSegments(
                                  item.chosenAnswerSegments,
                                  item.chosenAnswer,
                                )}
                              </Box>
                              {item.justification ? (
                                <Box
                                  sx={justificationBoxStyles(item.isCorrect)}
                                >
                                  {item.justification}
                                </Box>
                              ) : null}
                            </>
                          ) : hasMsqAnswers ? (
                            <>
                              <Box sx={answerListStyles}>
                                {item.answers!.map((answer) => {
                                  const variant = getFeedbackAnswerVariant(
                                    item,
                                    answer.text,
                                    answer.points,
                                  );
                                  const isChosen =
                                    variant === "chosen-correct" ||
                                    variant === "chosen-wrong";

                                  return (
                                    <Box
                                      key={answer.text}
                                      sx={answerOptionStyles(variant)}
                                    >
                                      {isChosen ? (
                                        <Typography
                                          sx={chosenAnswerLabelStyles(
                                            variant === "chosen-wrong"
                                              ? "chosen-wrong"
                                              : "chosen-correct",
                                          )}
                                        >
                                          Your answer
                                        </Typography>
                                      ) : null}
                                      <Box
                                        component="span"
                                        sx={{ display: "block" }}
                                      >
                                        {answer.text}
                                        {answer.points !== 0
                                          ? ` (+${answer.points} pts)`
                                          : ""}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                              {item.justification ? (
                                <Box
                                  sx={justificationBoxStyles(item.isCorrect)}
                                >
                                  {item.justification}
                                </Box>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <Typography sx={itemMetaStyles}>
                                Your answer: {item.chosenAnswer || "—"}
                              </Typography>
                              {item.justification ? (
                                <Box
                                  sx={justificationBoxStyles(item.isCorrect)}
                                >
                                  {item.justification}
                                </Box>
                              ) : null}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box sx={footerStyle}>
        <Box sx={footerActionsStyle}>
          <Button onClick={onReviewPerformance} sx={primaryActionStyle}>
            Review my performance
            <Box component="span" sx={{ ml: 1, display: "flex" }}>
              <ArrowLeft size={18} />
            </Box>
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={secondaryActionStyle}
          >
            Back to dashboard
            <Box component="span" sx={{ ml: 1, display: "flex" }}>
              <ArrowRight size={18} />
            </Box>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentContestResults;
